import { useRef } from 'react';
import { View } from 'foundations-components/components/View';
import { DO_NOT_DISPLAY_THREAD } from 'copilot-toolkit/constants/PromptThread';
import { INTERACTIVE_TOOL_BUTTON_CLICK } from 'copilot-toolkit/constants/Tools';
import { getAreAllParametersTheSame, getCurrentStateOfFunctionValuesByName, getShouldShowAsButton, getUndoAction } from 'copilot-toolkit/utils/interactiveModeUtils';
import { safeParse } from 'copilot-toolkit/utils/jsonSchemaUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ActionRenderer = ({
  message,
  actionFn,
  ChatSpotStyledButtonComponent,
  appName,
  sessionId,
  usageTracker,
  state
}) => {
  var _agentPayload$jsonCon, _agentPayload, _customFEActions;
  const agentPayload = safeParse((_agentPayload$jsonCon = message === null || message === void 0 || (_agentPayload = message.agentPayload) === null || _agentPayload === void 0 ? void 0 : _agentPayload.jsonContent) !== null && _agentPayload$jsonCon !== void 0 ? _agentPayload$jsonCon : '');
  const actionsToRender = agentPayload === null || agentPayload === void 0 ? void 0 : agentPayload.feInteractionsToExecute;
  const shouldShowAsButton = getShouldShowAsButton(actionsToRender, state === null || state === void 0 || (_customFEActions = state.customFEActions) === null || _customFEActions === void 0 ? void 0 : _customFEActions.interactions);
  // Explicitly cast to MutableRefObject so "current" is writable

  const undoActionRef = useRef({});
  // If a user clicks a button, we store that info as a key in the ref.

  if (!actionsToRender) {
    return null;
  }
  const messageId = 0;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: actionsToRender.map((action, idx) => {
      var _customFEActions2;
      const {
        functionName = '',
        buttonExplanation = '',
        buttonText = ''
      } = action || {};
      const uniqueKey = `${functionName}-${idx}`;
      const currentValuesOfFunction = getCurrentStateOfFunctionValuesByName(state === null || state === void 0 || (_customFEActions2 = state.customFEActions) === null || _customFEActions2 === void 0 ? void 0 : _customFEActions2.interactions, functionName);
      if (!currentValuesOfFunction) {
        return null;
      }
      const areAllParametersTheSame = getAreAllParametersTheSame(currentValuesOfFunction, action);
      const undoActionToInvoke = getUndoAction(currentValuesOfFunction, action);
      const undoAction = () => {
        actionFn({
          actionType: INTERACTIVE_TOOL_BUTTON_CLICK,
          actionToInvoke: undoActionToInvoke,
          // TODO: We should let the backend know that this was clicked if we want the message to persist.
          userThread: DO_NOT_DISPLAY_THREAD
        });
      };
      const handleOnClick = () => {
        actionFn({
          actionType: INTERACTIVE_TOOL_BUTTON_CLICK,
          actionToInvoke: action,
          // TODO: We should let the backend know that this was clicked if we want the message to persist.
          userThread: DO_NOT_DISPLAY_THREAD
        });
        // Only set it when the user clicks the button.
        undoActionRef.current[uniqueKey] = undoAction;
      };
      const disabled = areAllParametersTheSame && !undoActionRef.current[uniqueKey];

      // Set the undo action for when it happens automatically instant actions
      // Example: user says "make the color pink"
      if (!disabled && !shouldShowAsButton && !areAllParametersTheSame && !undoActionRef.current[uniqueKey]) {
        undoActionRef.current[uniqueKey] = undoAction;
        // Don't render the original button.
        return null;
      }
      return /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("i", {
            children: buttonExplanation
          })
        }), /*#__PURE__*/_jsx(ChatSpotStyledButtonComponent, {
          onClick: areAllParametersTheSame ? undoActionRef.current[uniqueKey] : handleOnClick,
          usageTracker: usageTracker,
          usageTrackerAppLocationKey: "copilot-plugins",
          eventName: "collaborationModeInteractions"
          // TODO: fix tracking.  Maybe use regular button
          ,
          eventProperties: {
            // Add the action?
            action: 'interactive-mode-action-clicked',
            interactionType: 'copilot-sidebar-interaction',
            messageId,
            featureCategoryId: 'intent',
            sessionId,
            inApp: appName
          },
          disabled: disabled,
          children: areAllParametersTheSame ? 'Undo' : buttonText
        }, uniqueKey)]
      }, uniqueKey);
    })
  });
};

// We may eventually style these responses uniquely, but for now using standard copilot responses.
export const InteractiveModePlugin = {
  gate: 'Copilot:Collaborate',
  intentTypes: ['interactive_mode'],
  collaborateCopilotMessageTypes: [],
  // Default rendering for now.
  sectionRenderer: null,
  actionsRenderer: ActionRenderer
};